import React, { useEffect, useState } from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';

/**
 * WordPress OAuth Callback Handler
 * 
 * This component handles OAuth redirects specifically for WordPress plugin integrations.
 * It runs ONLY when opened in a popup window from WordPress admin.
 * 
 * Flow:
 * 1. WordPress opens popup to: /wordpress-oauth-callback
 * 2. User authenticates with Google
 * 3. Supabase redirects back here with tokens in URL fragment
 * 4. This component extracts tokens and sends to parent window (WordPress)
 * 5. Popup closes automatically
 * 
 * IMPORTANT: This does NOT trigger normal onboarding flow.
 * It's completely isolated from regular user signup/login.
 */
function WordPressOAuthCallback() {
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    console.log('[WordPressOAuthCallback] Component mounted');
    
    // Check if this page was opened in a popup (has a parent window)
    if (window.opener && !window.opener.closed) {
      console.log('[WordPressOAuthCallback] Running in popup mode');
      setStatus('Completing authentication...');
      
      // Extract tokens from URL fragment (#access_token=xxx&refresh_token=yyy)
      const hash = window.location.hash.substring(1); // Remove leading #
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const expiresIn = params.get('expires_in');
      const expiresAt = params.get('expires_at');
      
      console.log('[WordPressOAuthCallback] Extracted tokens:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        expiresIn,
        expiresAt
      });
      
      if (accessToken) {
        // Send tokens to parent window (WordPress admin)
        // Using postMessage for secure cross-origin communication
        try {
          window.opener.postMessage({
            type: 'COMTURK_OAUTH_SUCCESS',
            accessToken: accessToken,
            refreshToken: refreshToken,
            expiresIn: expiresIn,
            expiresAt: expiresAt,
            timestamp: Date.now()
          }, '*'); // WordPress will validate the message
          
          console.log('[WordPressOAuthCallback] Tokens sent to parent window');
          setStatus('Authentication successful! Closing...');
          
          // Close popup after a brief delay (gives WordPress time to receive message)
          setTimeout(() => {
            console.log('[WordPressOAuthCallback] Closing popup');
            window.close();
          }, 500);
          
        } catch (error) {
          console.error('[WordPressOAuthCallback] Error sending message to parent:', error);
          setStatus('Error communicating with WordPress. Please close this window.');
          
          // Try to close anyway after a longer delay
          setTimeout(() => window.close(), 3000);
        }
        
      } else {
        // No access token found in URL
        console.error('[WordPressOAuthCallback] No access token in URL fragment');
        setStatus('Authentication failed - no token received.');
        
        // Notify parent of error
        try {
          window.opener.postMessage({
            type: 'COMTURK_OAUTH_ERROR',
            error: 'No access token received',
            timestamp: Date.now()
          }, '*');
        } catch (error) {
          console.error('[WordPressOAuthCallback] Error sending error to parent:', error);
        }
        
        // Close after showing error
        setTimeout(() => window.close(), 2000);
      }
      
    } else {
      // Not opened in a popup - this shouldn't happen in normal WordPress flow
      // Redirect to homepage to avoid confusion
      console.log('[WordPressOAuthCallback] Not in popup mode, redirecting to homepage');
      setStatus('Redirecting...');
      
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  }, []);

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      alignItems="center" 
      justifyContent="center" 
      minHeight="100vh"
      sx={{ backgroundColor: '#f5f5f5' }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
        {status}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.disabled' }}>
        Please wait...
      </Typography>
    </Box>
  );
}

export default WordPressOAuthCallback;


